import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Upload, CheckCircle, AlertCircle, FileText, X, Download, Trash2 } from 'lucide-react';
import studyAbroadService from '../../services/studyAbroadService';
import PaymentGate from './PaymentGate';

const DocumentsSection = ({ profile, onRefresh }) => {
  const [documents, setDocuments] = useState([]);
  const [uploadingDocType, setUploadingDocType] = useState(null);
  const [uploadingFileName, setUploadingFileName] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passportInfo, setPassportInfo] = useState({
    passportNumber: '',
    passportExpiryDate: ''
  });
  const [savingPassport, setSavingPassport] = useState(false);

  useEffect(() => {
    if (profile?.documents) {
      setDocuments(profile.documents);
    }
    if (profile?.personalDetails) {
      setPassportInfo({
        passportNumber: profile.personalDetails.passportNumber || '',
        passportExpiryDate: profile.personalDetails.passportExpiryDate ? profile.personalDetails.passportExpiryDate.split('T')[0] : ''
      });
    }
  }, [profile]);

  // Check if section is locked
  if (profile?.paymentStatus !== 'paid') {
    return <PaymentGate profile={profile} sectionName="Documents" />;
  }

  // Required document types
  const requiredDocuments = [
    { type: 'passport', label: 'Passport', description: 'Valid passport (all pages)' },
    { type: 'academic_transcripts', label: 'Academic Transcripts', description: 'Official transcripts from all institutions' },
    { type: 'degree_certificates', label: 'Degree Certificates', description: 'Certificates for completed degrees' },
    { type: 'english_test', label: 'English Proficiency Test', description: 'IELTS/TOEFL/PTE results (if available)' },
    { type: 'cv', label: 'CV/Resume', description: 'Current curriculum vitae' },
    { type: 'personal_statement', label: 'Personal Statement', description: 'Draft personal statement (if available)' },
    { type: 'reference_letters', label: 'Reference Letters', description: 'Academic or professional references (if available)' },
    { type: 'other', label: 'Other Documents', description: 'Any other relevant documents' }
  ];

  const handlePassportChange = (e) => {
    const { name, value } = e.target;
    setPassportInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSavePassport = async () => {
    setSavingPassport(true);
    setErrorMessage('');
    
    try {
      await studyAbroadService.updatePersonalDetails({
        passportNumber: passportInfo.passportNumber,
        passportExpiryDate: passportInfo.passportExpiryDate
      });
      setSuccessMessage('Passport information saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      if (onRefresh) onRefresh();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to save passport information');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setSavingPassport(false);
    }
  };

  const handleFileUpload = async (documentType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage('File size must be less than 10MB');
      setTimeout(() => setErrorMessage(''), 5000);
      event.target.value = '';
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage('Only PDF, JPG, PNG, and DOC/DOCX files are allowed');
      setTimeout(() => setErrorMessage(''), 5000);
      event.target.value = '';
      return;
    }

    setUploadingDocType(documentType);
    setUploadingFileName(file.name);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', documentType);

      await studyAbroadService.uploadDocument(formData);
      
      setSuccessMessage(`✓ ${file.name} uploaded successfully!`);
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Refresh to show new document
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Upload error:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to upload document. Please try again.');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setUploadingDocType(null);
      setUploadingFileName('');
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await studyAbroadService.deleteDocument(documentId);
      setSuccessMessage('Document deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      if (onRefresh) onRefresh();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to delete document');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const getDocumentsByType = (type) => {
    return documents.filter(doc => doc.documentType === type);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'rejected':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'pending':
        return <AlertCircle size={16} className="text-yellow-600" />;
      case 'rejected':
        return <X size={16} className="text-red-600" />;
      default:
        return <FileText size={16} className="text-gray-600" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#202356] mb-2">Documents</h1>
          <p className="text-gray-600">
            Upload your required documents for application processing
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
          >
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-green-700">{successMessage}</p>
          </motion.div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </motion.div>
        )}

        {/* Instructions */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Upload Guidelines</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Accepted formats: PDF, JPG, PNG, DOC, DOCX</li>
            <li>• Maximum file size: 10MB per document</li>
            <li>• Ensure all documents are clear and readable</li>
            <li>• You can upload multiple files for each document type</li>
          </ul>
        </div>

        {/* Passport Information */}
        <div className="mb-6 bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Passport Information (Optional)</h2>
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> You can provide your passport details now or add them later when available.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passport Number
              </label>
              <input
                type="text"
                name="passportNumber"
                value={passportInfo.passportNumber}
                onChange={handlePassportChange}
                placeholder="A12345678 (optional)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passport Expiry Date
              </label>
              <input
                type="date"
                name="passportExpiryDate"
                value={passportInfo.passportExpiryDate}
                onChange={handlePassportChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSavePassport}
              disabled={savingPassport || (!passportInfo.passportNumber && !passportInfo.passportExpiryDate)}
              className="flex items-center gap-2 px-6 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingPassport ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  {passportInfo.passportNumber || passportInfo.passportExpiryDate ? 'Update' : 'Save'} Passport Info
                </>
              )}
            </button>
          </div>
        </div>

        {/* Document Upload Sections */}
        <div className="space-y-6">
          {requiredDocuments.map((docType) => {
            const uploadedDocs = getDocumentsByType(docType.type);
            const isRequired = ['passport', 'academic_transcripts', 'degree_certificates', 'cv'].includes(docType.type);

            return (
              <div key={docType.type} className="bg-white rounded-lg border border-gray-200 p-6 hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {docType.label}
                      </h3>
                      {isRequired && <span className="text-red-500 text-sm">*</span>}
                      {uploadedDocs.length > 0 && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          {uploadedDocs.length} uploaded
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{docType.description}</p>
                  </div>
                  
                  {/* Upload Button */}
                  <label 
                    htmlFor={`file-input-${docType.type}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all cursor-pointer ${
                      uploadingDocType === docType.type
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-[#202356] text-white hover:bg-[#2542D4] hover:shadow-md'
                    }`}>
                    <Upload size={18} />
                    {uploadingDocType === docType.type ? 'Uploading...' : uploadedDocs.length > 0 ? 'Add More' : 'Upload'}
                  </label>
                  <input
                    id={`file-input-${docType.type}`}
                    type="file"
                    onChange={(e) => handleFileUpload(docType.type, e)}
                    className="hidden"
                    disabled={uploadingDocType === docType.type}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                </div>

                {/* Uploaded Documents List */}
                {uploadedDocs.length > 0 ? (
                  <div className="space-y-3 mt-4">
                    {uploadedDocs.map((doc) => (
                      <div
                        key={doc._id}
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className="p-2 bg-blue-50 rounded-lg">
                            <FileText className="text-blue-600 flex-shrink-0" size={24} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{doc.fileName}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-gray-500">{formatFileSize(doc.fileSize)}</span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">
                                {new Date(doc.uploadedAt).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          {/* Status Badge */}
                          <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-medium ${getStatusColor(doc.status)}`}>
                            {getStatusIcon(doc.status)}
                            <span className="capitalize">{doc.status}</span>
                          </div>

                          {/* Download Button */}
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-600 hover:text-[#202356] hover:bg-blue-50 rounded-lg transition-colors"
                            title="Download/View"
                          >
                            <Download size={18} />
                          </a>

                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteDocument(doc._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete document"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <label 
                    htmlFor={`file-input-${docType.type}`}
                    className="mt-4 p-6 border-2 border-dashed border-gray-300 rounded-lg text-center bg-gray-50 hover:bg-gray-100 hover:border-[#202356] transition-all cursor-pointer block"
                  >
                    <FileText className="mx-auto text-gray-400 mb-2" size={40} />
                    <p className="text-sm font-medium text-gray-600 mb-1">No documents uploaded yet</p>
                    <p className="text-xs text-gray-500">Click here or the upload button above to add files</p>
                  </label>
                )}
              </div>
            );
          })}
        </div>

        {/* Upload Progress Indicator */}
        {uploadingDocType && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-4 right-4 bg-white rounded-lg shadow-xl border border-blue-200 p-4 max-w-sm z-50"
          >
            <div className="flex items-start gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#202356] mt-0.5"></div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900 mb-1">Uploading document...</p>
                <p className="text-xs text-gray-600 truncate">{uploadingFileName}</p>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default DocumentsSection;
